import clsx from 'clsx';
import { Table2 } from 'lucide-react';
import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

/** Width of an element, kept up to date as the window or a side panel resizes. */
export function useWidth(ref: RefObject<HTMLElement | null>, fallback = 600): number {
  const [w, setW] = useState(fallback);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setW(Math.round(el.getBoundingClientRect().width) || fallback);
    const ro = new ResizeObserver(([e]) => {
      const next = Math.round(e?.contentRect.width ?? 0);
      if (next > 0) setW(next);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref, fallback]);
  return w;
}

/** A top value and step that give four or five clean gridlines: 0, 50, 100, 150, 200. */
export function niceScale(max: number, ticks = 4): { max: number; step: number } {
  if (!(max > 0)) return { max: ticks, step: 1 };
  const raw = max / ticks;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const frac = raw / pow;
  const step = (frac <= 1 ? 1 : frac <= 2 ? 2 : frac <= 2.5 ? 2.5 : frac <= 5 ? 5 : 10) * pow;
  return { max: step * Math.ceil(max / step), step };
}

/** ₹1.2K, ₹3.5L, ₹1.2Cr — short enough for an axis. Takes paise. */
export function compactRupees(paise: number): string {
  const r = Math.abs(paise) / 100;
  const sign = paise < 0 ? '−' : '';
  const trim = (n: number) => String(Number(n.toFixed(1)));
  if (r >= 1e7) return `${sign}₹${trim(r / 1e7)}Cr`;
  if (r >= 1e5) return `${sign}₹${trim(r / 1e5)}L`;
  if (r >= 1e3) return `${sign}₹${trim(r / 1e3)}K`;
  return `${sign}₹${Math.round(r)}`;
}

export const dayLabel = (iso: string, withYear = false) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    ...(withYear ? { year: 'numeric' } : {}),
  });

/** One tooltip for every chart: the title, then a value and name for each series. */
interface TipState {
  x: number;
  y: number;
  title: string;
  rows: { name: string; value: string; color?: string }[];
}

function Tooltip({ tip, width }: { tip: TipState | null; width: number }) {
  if (!tip) return null;
  const flip = tip.x > width * 0.6;
  return (
    <div
      role="status"
      className="pointer-events-none absolute z-20 min-w-36 rounded-lg border border-line bg-paper px-3 py-2 text-[12px] shadow-lg"
      style={{
        left: tip.x,
        top: Math.max(0, tip.y),
        transform: `translate(${flip ? 'calc(-100% - 12px)' : '12px'}, 0)`,
      }}
    >
      <div className="mb-1 text-slate">{tip.title}</div>
      {tip.rows.map((r) => (
        <div key={r.name} className="flex items-center gap-2">
          {r.color ? (
            <span className="h-0.5 w-3 rounded-full" style={{ background: r.color }} aria-hidden />
          ) : null}
          <span className="tabular text-[14px] font-semibold text-ink">{r.value}</span>
          <span className="text-slate">{r.name}</span>
        </div>
      ))}
    </div>
  );
}

/** A titled card with a switch to see the same numbers as a table. */
export function ChartCard({
  title,
  subtitle,
  legend,
  table,
  className,
  children,
}: {
  title: string;
  subtitle?: string;
  legend?: { label: string; color: string }[];
  table: { head: string[]; rows: (string | number)[][] };
  className?: string;
  children: ReactNode;
}) {
  const [asTable, setAsTable] = useState(false);
  return (
    <section
      className={clsx(
        'viz-root rounded-xl border border-line bg-[var(--surface-1)] p-4',
        className,
      )}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-[14px] font-semibold text-ink">{title}</h2>
          {subtitle ? <p className="text-[12px] text-slate">{subtitle}</p> : null}
        </div>
        <button
          type="button"
          aria-pressed={asTable}
          title={asTable ? 'Show the chart' : 'Show as a table'}
          onClick={() => setAsTable((v) => !v)}
          className={clsx(
            'inline-flex items-center gap-1 rounded-md px-2 py-1 text-[12px] text-slate hover:bg-canvas hover:text-ink',
            asTable && 'bg-canvas text-ink',
          )}
        >
          <Table2 size={13} /> {asTable ? 'Chart' : 'Table'}
        </button>
      </header>
      {legend && legend.length > 1 && !asTable ? (
        <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-slate">
          {legend.map((l) => (
            <li key={l.label} className="inline-flex items-center gap-1.5">
              <span
                className="h-2.5 w-2.5 rounded-sm"
                style={{ background: l.color }}
                aria-hidden
              />
              {l.label}
            </li>
          ))}
        </ul>
      ) : null}
      <div className="mt-3">
        {asTable ? (
          <div className="max-h-72 overflow-auto">
            <table className="w-full text-left text-[13px]">
              <thead className="text-[12px] text-slate">
                <tr>
                  {table.head.map((h, i) => (
                    <th
                      key={h}
                      className={clsx(
                        'border-b border-line py-1.5 font-medium',
                        i > 0 && 'text-right',
                      )}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((r) => (
                  <tr key={String(r[0])}>
                    {r.map((c, i) => (
                      <td
                        // biome-ignore lint/suspicious/noArrayIndexKey: a table cell has no identity of its own
                        key={i}
                        className={clsx(
                          'tabular border-b border-line/60 py-1',
                          i > 0 && 'text-right',
                        )}
                      >
                        {c}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          children
        )}
      </div>
    </section>
  );
}

const PAD = { l: 44, r: 16, t: 10, b: 26 };
/** Room on the right of a line chart for the names at the ends of the lines. */
const LINE_PAD = { ...PAD, r: 64 };

interface Series {
  key: string;
  label: string;
  color: string;
  values: number[];
}

/** Evenly spread x labels that never run into each other. */
function xTicks(n: number, plotW: number, labelW = 52): number[] {
  if (n <= 1) return [0];
  const fit = Math.max(2, Math.floor(plotW / labelW));
  const every = Math.max(1, Math.ceil((n - 1) / (fit - 1)));
  const out: number[] = [];
  for (let i = 0; i < n; i += every) out.push(i);
  return out;
}

/** Lines over time with a crosshair that snaps to the nearest day and reads out every series. */
export function LineChart({
  days,
  series,
  format = (n) => String(n),
  height = 240,
  label,
}: {
  days: string[];
  series: Series[];
  format?: (n: number) => string;
  height?: number;
  label: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const width = useWidth(ref);
  const [at, setAt] = useState<number | null>(null);
  const n = days.length;
  const plotW = Math.max(40, width - LINE_PAD.l - LINE_PAD.r);
  const plotH = height - LINE_PAD.t - LINE_PAD.b;
  const { max, step } = niceScale(Math.max(0, ...series.flatMap((s) => s.values)));
  const x = (i: number) => LINE_PAD.l + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const y = (v: number) => LINE_PAD.t + plotH - (v / max) * plotH;
  const grid = Array.from({ length: Math.round(max / step) + 1 }, (_, i) => i * step);

  const pick = useCallback(
    (clientX: number) => {
      const box = ref.current?.getBoundingClientRect();
      if (!box || n === 0) return;
      const rel = (clientX - box.left - LINE_PAD.l) / plotW;
      setAt(Math.min(n - 1, Math.max(0, Math.round(rel * (n - 1)))));
    },
    [n, plotW],
  );

  // End labels only when the lines finish far enough apart; the legend covers the rest.
  const ends = series.map((s) => ({ s, y: y(s.values[n - 1] ?? 0) }));
  const direct =
    series.length <= 4 &&
    ends.every((a, i) => ends.every((b, j) => i === j || Math.abs(a.y - b.y) >= 14));

  const tip: TipState | null =
    at === null
      ? null
      : {
          x: x(at),
          y: LINE_PAD.t,
          title: dayLabel(days[at] as string, true),
          rows: series.map((s) => ({
            name: s.label,
            value: format(s.values[at] ?? 0),
            color: s.color,
          })),
        };

  return (
    <div ref={ref} className="relative" style={{ height }}>
      <svg width={width} height={height} role="img" aria-label={label} className="block">
        {grid.map((g) => (
          <g key={g}>
            <line
              x1={LINE_PAD.l}
              x2={LINE_PAD.l + plotW}
              y1={y(g)}
              y2={y(g)}
              stroke="var(--viz-grid)"
            />
            <text
              x={LINE_PAD.l - 8}
              y={y(g)}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize={11}
              fill="var(--viz-muted)"
            >
              {format(g)}
            </text>
          </g>
        ))}
        <line
          x1={LINE_PAD.l}
          x2={LINE_PAD.l + plotW}
          y1={y(0)}
          y2={y(0)}
          stroke="var(--viz-axis)"
        />
        {xTicks(n, plotW).map((i) => (
          <text
            key={i}
            x={x(i)}
            y={height - 6}
            textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}
            fontSize={11}
            fill="var(--viz-muted)"
          >
            {dayLabel(days[i] as string)}
          </text>
        ))}
        {at !== null ? (
          <line x1={x(at)} x2={x(at)} y1={LINE_PAD.t} y2={y(0)} stroke="var(--viz-axis)" />
        ) : null}
        {series.map((s) => (
          <g key={s.key}>
            {series.length === 1 && n > 1 ? (
              <path
                d={`M${x(0)},${y(0)} ${s.values.map((v, i) => `L${x(i)},${y(v)}`).join(' ')} L${x(n - 1)},${y(0)} Z`}
                fill={s.color}
                opacity={0.1}
              />
            ) : null}
            <polyline
              fill="none"
              stroke={s.color}
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              points={s.values.map((v, i) => `${x(i)},${y(v)}`).join(' ')}
            />
            {(at === null ? [n - 1] : [at]).map((i) => (
              <circle
                key={i}
                cx={x(i)}
                cy={y(s.values[i] ?? 0)}
                r={4}
                fill={s.color}
                stroke="var(--surface-1)"
                strokeWidth={2}
              />
            ))}
          </g>
        ))}
        {direct && at === null
          ? ends.map(({ s, y: ey }) => (
              <text
                key={s.key}
                x={x(n - 1) + 10}
                y={ey}
                dominantBaseline="middle"
                textAnchor="start"
                fontSize={11}
                fill="var(--viz-muted)"
              >
                {s.label}
              </text>
            ))
          : null}
        {/* A wide invisible target so the pointer only has to be near a day. */}
        <rect
          x={LINE_PAD.l}
          y={LINE_PAD.t}
          width={plotW}
          height={plotH}
          fill="transparent"
          role="slider"
          tabIndex={0}
          aria-valuemin={0}
          aria-valuemax={Math.max(0, n - 1)}
          aria-valuenow={at ?? Math.max(0, n - 1)}
          aria-valuetext={days[at ?? n - 1] ? dayLabel(days[at ?? n - 1] as string, true) : ''}
          aria-orientation="horizontal"
          aria-label={`${label}. Use the left and right arrow keys to read each day.`}
          onPointerMove={(e) => pick(e.clientX)}
          onPointerLeave={() => setAt(null)}
          onFocus={() => setAt((a) => a ?? n - 1)}
          onBlur={() => setAt(null)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') setAt((a) => Math.max(0, (a ?? n) - 1));
            else if (e.key === 'ArrowRight') setAt((a) => Math.min(n - 1, (a ?? -1) + 1));
            else if (e.key === 'Escape') setAt(null);
          }}
        />
      </svg>
      <Tooltip tip={tip} width={width} />
    </div>
  );
}

/** One series as columns over time. Each column is its own hover and focus target. */
export function ColumnChart({
  days,
  values,
  color,
  name,
  format,
  height = 220,
  label,
}: {
  days: string[];
  values: number[];
  color: string;
  name: string;
  format: (n: number) => string;
  height?: number;
  label: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const width = useWidth(ref);
  const [at, setAt] = useState<number | null>(null);
  const n = days.length;
  const plotW = Math.max(40, width - PAD.l - PAD.r);
  const plotH = height - PAD.t - PAD.b;
  const { max, step } = niceScale(Math.max(0, ...values));
  const band = plotW / Math.max(1, n);
  const bar = Math.max(1, Math.min(24, band - Math.max(2, band * 0.25)));
  const y = (v: number) => PAD.t + plotH - (v / max) * plotH;
  const grid = Array.from({ length: Math.round(max / step) + 1 }, (_, i) => i * step);
  const cx = (i: number) => PAD.l + band * i + band / 2;
  const top = values.indexOf(Math.max(...values, 0));

  const tip: TipState | null =
    at === null
      ? null
      : {
          x: cx(at),
          y: Math.max(PAD.t, y(values[at] ?? 0) - 8),
          title: dayLabel(days[at] as string, true),
          rows: [{ name, value: format(values[at] ?? 0), color }],
        };

  return (
    <div ref={ref} className="relative" style={{ height }}>
      <svg width={width} height={height} role="img" aria-label={label} className="block">
        {grid.map((g) => (
          <g key={g}>
            <line x1={PAD.l} x2={PAD.l + plotW} y1={y(g)} y2={y(g)} stroke="var(--viz-grid)" />
            <text
              x={PAD.l - 8}
              y={y(g)}
              textAnchor="end"
              dominantBaseline="middle"
              fontSize={11}
              fill="var(--viz-muted)"
            >
              {format(g)}
            </text>
          </g>
        ))}
        <line x1={PAD.l} x2={PAD.l + plotW} y1={y(0)} y2={y(0)} stroke="var(--viz-axis)" />
        {xTicks(n, plotW).map((i) => (
          <text
            key={i}
            x={cx(i)}
            y={height - 6}
            textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}
            fontSize={11}
            fill="var(--viz-muted)"
          >
            {dayLabel(days[i] as string)}
          </text>
        ))}
        {values.map((v, i) => {
          const h = Math.max(0, y(0) - y(v));
          const r = Math.min(4, bar / 2, h);
          const x0 = cx(i) - bar / 2;
          return (
            // biome-ignore lint/a11y/useSemanticElements: an SVG group cannot be a <button>
            <g
              key={days[i]}
              tabIndex={0}
              role="button"
              aria-label={`${dayLabel(days[i] as string, true)}: ${format(v)} ${name}`}
              onPointerEnter={() => setAt(i)}
              onPointerLeave={() => setAt(null)}
              onFocus={() => setAt(i)}
              onBlur={() => setAt(null)}
            >
              <rect x={PAD.l + band * i} y={PAD.t} width={band} height={plotH} fill="transparent" />
              {h > 0 ? (
                <path
                  d={`M${x0},${y(0)} V${y(v) + r} Q${x0},${y(v)} ${x0 + r},${y(v)} H${x0 + bar - r} Q${x0 + bar},${y(v)} ${x0 + bar},${y(v) + r} V${y(0)} Z`}
                  fill={color}
                  opacity={at === null || at === i ? 1 : 0.55}
                />
              ) : null}
            </g>
          );
        })}
        {at === null && values[top] ? (
          <text
            x={cx(top)}
            y={y(values[top] ?? 0) - 6}
            textAnchor={cx(top) > width - 60 ? 'end' : 'middle'}
            fontSize={11}
            fill="var(--viz-muted)"
          >
            {format(values[top] ?? 0)}
          </text>
        ) : null}
      </svg>
      <Tooltip tip={tip} width={width} />
    </div>
  );
}

export interface BarRow {
  key: string;
  label: string;
  value: number;
  icon?: ReactNode;
  /** Overrides the bar colour; used only for reserved status colours. */
  color?: string;
}

/** Horizontal bars: names on the left, the value at the tip of each bar. */
export function BarList({
  rows,
  format = (n) => n.toLocaleString('en-IN'),
  color = 'var(--series-1)',
  unit,
  label,
}: {
  rows: BarRow[];
  format?: (n: number) => string;
  color?: string;
  /** What is counted, for the tooltip, e.g. "reports". */
  unit: string;
  label: string;
}) {
  const ref = useRef<HTMLUListElement>(null);
  const width = useWidth(ref as RefObject<HTMLElement | null>, 400);
  const [tip, setTip] = useState<TipState | null>(null);
  const max = Math.max(1, ...rows.map((r) => r.value));
  const total = rows.reduce((n, r) => n + r.value, 0);
  const show = (r: BarRow, el: HTMLElement) => {
    const box = ref.current?.getBoundingClientRect();
    const b = el.getBoundingClientRect();
    setTip({
      x: Math.min(width - 8, b.left - (box?.left ?? 0) + Math.max(60, b.width * 0.5)),
      y: b.top - (box?.top ?? 0) - 6,
      title: r.label,
      rows: [
        {
          name: `${unit}${total ? ` · ${Math.round((r.value / total) * 100)}%` : ''}`,
          value: format(r.value),
          color: r.color ?? color,
        },
      ],
    });
  };
  return (
    <ul ref={ref} aria-label={label} className="relative grid grid-cols-[minmax(0,1fr)] gap-1.5">
      {rows.map((r) => (
        <li
          key={r.key}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: each bar is a keyboard target for its tooltip
          tabIndex={0}
          className="grid grid-cols-[minmax(6.5rem,42%)_minmax(0,1fr)] items-center gap-3 rounded-md py-0.5 hover:bg-canvas focus:bg-canvas focus:outline-none"
          onPointerEnter={(e) => show(r, e.currentTarget)}
          onPointerLeave={() => setTip(null)}
          onFocus={(e) => show(r, e.currentTarget)}
          onBlur={() => setTip(null)}
        >
          <span className="flex min-w-0 items-center gap-1.5 text-[13px] text-ink">
            {r.icon ? <span className="shrink-0 text-slate">{r.icon}</span> : null}
            <span className="min-w-0 leading-tight break-words">{r.label}</span>
          </span>
          <span className="relative block h-5">
            <span
              className="absolute top-1/2 left-0 h-2.5 -translate-y-1/2 rounded-r-[4px]"
              style={{
                width: `max(${r.value > 0 ? 3 : 0}px, calc((100% - 4.5rem) * ${r.value / max}))`,
                background: r.color ?? color,
              }}
            />
            <span
              className="tabular absolute top-1/2 -translate-y-1/2 pl-2 text-[13px] font-medium whitespace-nowrap text-ink"
              style={{
                left: `max(${r.value > 0 ? 3 : 0}px, calc((100% - 4.5rem) * ${r.value / max}))`,
              }}
            >
              {format(r.value)}
            </span>
          </span>
        </li>
      ))}
      <Tooltip tip={tip} width={width} />
    </ul>
  );
}

/** A ratio against a limit: the filled part on a lighter track of the same colour. */
export function Meter({ fraction, label }: { fraction: number; label: string }) {
  const pct = Math.max(0, Math.min(1, fraction));
  return (
    // biome-ignore lint/a11y/useSemanticElements: <meter> cannot be styled with a lighter track
    <div
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct * 100)}
      className="h-2.5 w-full overflow-hidden rounded-full"
      style={{ background: 'var(--seq-track)' }}
    >
      <div
        className="h-full rounded-full"
        style={{ width: `${pct * 100}%`, background: 'var(--seq-fill)' }}
      />
    </div>
  );
}

/** A small trend line for a stat tile. The latest point is the accent. */
export function Sparkline({
  values,
  label,
  w = 96,
  h = 28,
}: {
  values: number[];
  label: string;
  w?: number;
  h?: number;
}) {
  const max = Math.max(1, ...values);
  const n = values.length;
  if (n < 2) return null;
  const x = (i: number) => 3 + (i / (n - 1)) * (w - 6);
  const y = (v: number) => h - 4 - (v / max) * (h - 8);
  return (
    <svg width={w} height={h} role="img" aria-label={label} className="viz-root shrink-0">
      <polyline
        fill="none"
        stroke="var(--viz-muted)"
        strokeWidth={2}
        strokeLinejoin="round"
        strokeLinecap="round"
        points={values.map((v, i) => `${x(i)},${y(v)}`).join(' ')}
      />
      <circle
        cx={x(n - 1)}
        cy={y(values[n - 1] ?? 0)}
        r={3.5}
        fill="var(--series-1)"
        stroke="var(--surface-1)"
        strokeWidth={2}
      />
    </svg>
  );
}

/** A sparkline that fills the width it is given. */
export function WideSparkline({
  values,
  label,
  h = 56,
}: {
  values: number[];
  label: string;
  h?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const w = useWidth(ref, 240);
  return (
    <div ref={ref} className="min-w-0">
      <Sparkline values={values} label={label} w={w} h={h} />
    </div>
  );
}

/** Close any open popover when the pointer goes elsewhere or Escape is pressed. */
export function useDismiss(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const down = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    const key = (e: KeyboardEvent) => e.key === 'Escape' && close();
    document.addEventListener('pointerdown', down);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('pointerdown', down);
      document.removeEventListener('keydown', key);
    };
  }, [open, close]);
  return ref;
}
